import { useCallback, useEffect, useRef, useState } from 'react';
import type { CameraPosition } from '../sections/sections';
import { sections } from '../sections/sections';
import './VideoBackground.css';

/**
 * Inline LQIP (Low Quality Image Placeholder).
 * Renders on the very first paint before any network request completes.
 * A 32px-wide blurred frame extracted from the video.
 */
const LQIP =
  'data:image/jpeg;base64,/9j//gAQTGF2YzYyLjI4LjEwMAD/2wBDAAgUFBcUFxsbGxsbGyAeICEhISAgICAhISEkJCQqKiokJCQhISQkKCgqKi4vLisrKisvLzIyMjw8OTlGRkhWVmf/xABsAAADAQEBAAAAAAAAAAAAAAAFAwIEAQcBAAMBAQAAAAAAAAAAAAAAAAABAgQDEAABAgMHAwUBAQAAAAAAAAABAgARAyFBEiIycZGBBDFR0UJyM+FhExEBAQEBAAAAAAAAAAAAAAAAAAERQv/AABEIABIAIAMBIgACEQADEQD/2gAMAwEAAhEDEQA/APDntuAARPevDXKCCcRMP5b6MxFE2CIolJSY1xH90DHG0JUi6Igg+K12fUyoi8pQSN1HQM2r/JF0y0kptXGu3teVcvDeTiFvkahidBRlVw3JaRkVw3htY302ZQsKe1jmQSJwhSrrps5+LiT9w1aZr0//2Q==';

interface VideoBackgroundProps {
  videoSrc: string;
  posterSrc?: string;
  /** WebP poster — paired with `posterSrc` (JPEG) for `image-set` + preload */
  posterWebpSrc?: string;
  /** WebM source — preferred format, ~70% smaller than MP4 */
  webmSrc?: string;
}

/* ---- math helpers ---- */

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Linear blend between section cameras — scroll progress is already tied to real DOM range. */
function lerpCamera(from: CameraPosition, to: CameraPosition, t: number): CameraPosition {
  const e = Math.max(0, Math.min(1, t));
  return {
    x: lerp(from.x, to.x, e),
    y: lerp(from.y, to.y, e),
    scale: lerp(from.scale, to.scale, e),
    rotate: lerp(from.rotate ?? 0, to.rotate ?? 0, e),
  };
}

function cameraDistance(a: CameraPosition, b: CameraPosition): number {
  const dr = (a.rotate ?? 0) - (b.rotate ?? 0);
  return Math.hypot(a.x - b.x, a.y - b.y, a.scale - b.scale, dr);
}

/** Per-frame smoothing toward target (alpha ~0.14–0.18 feels steady at 60fps). */
function dampCamera(current: CameraPosition, target: CameraPosition, alpha: number): CameraPosition {
  return {
    x: lerp(current.x, target.x, alpha),
    y: lerp(current.y, target.y, alpha),
    scale: lerp(current.scale, target.scale, alpha),
    rotate: lerp(current.rotate ?? 0, target.rotate ?? 0, alpha),
  };
}

/** Returns true if the user's connection is too slow for video. */
function isSlowConnection(): boolean {
  const conn = (
    navigator as unknown as { connection?: { effectiveType?: string; saveData?: boolean } }
  ).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  return conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g';
}

/**
 * Full-screen parallax video background with scroll-driven camera.
 *
 * Loading strategy (3 tiers):
 *   1. LQIP — tiny blurred image baked into JS, paints instantly
 *   2. Poster — 172KB JPEG, preloaded in <head>, replaces LQIP
 *   3. Video — WebM (2.5MB) preferred, MP4 (17MB) fallback
 *      Skipped entirely on slow connections (poster stays).
 *      Poster uses WebP when `posterWebpSrc` is set, with JPEG fallback.
 *
 * Camera interpolates continuously with scroll via rAF + direct DOM
 * manipulation (no React re-renders during scroll).
 * Progress follows real section `offsetTop` ranges (not scrollY / vh), with
 * light damping so motion doesn’t feel twitchy or “cut” at segment boundaries.
 */
export function VideoBackground({ videoSrc, posterSrc, posterWebpSrc, webmSrc }: VideoBackgroundProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const posterRef = useRef<HTMLDivElement>(null);
  const vignetteRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const smoothedRef = useRef<CameraPosition | null>(null);
  const sectionTopsRef = useRef<number[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);
  const skipVideo = useRef(isSlowConnection());

  // Preload the poster image (WebP first, JPEG fallback)
  useEffect(() => {
    if (!posterSrc && !posterWebpSrc) return undefined;
    const img = new Image();
    let triedJpegFallback = false;
    img.onload = () => setPosterLoaded(true);
    img.onerror = () => {
      if (posterWebpSrc && posterSrc && !triedJpegFallback) {
        triedJpegFallback = true;
        img.src = posterSrc;
      }
    };
    img.src = posterWebpSrc || posterSrc || '';
    return undefined;
  }, [posterSrc, posterWebpSrc]);

  const handleCanPlay = useCallback(() => {
    setIsReady(true);
    videoRef.current?.play().catch(() => {});
  }, []);

  // Load video after poster is visible (or after 2s as fallback)
  useEffect(() => {
    if (skipVideo.current) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const startLoad = () => {
      video.preload = 'auto';
      video.load();
    };

    // If poster already loaded, start video immediately; otherwise wait 2s
    const delay = posterLoaded ? 0 : 2000;
    const timer = setTimeout(startLoad, delay);
    return () => clearTimeout(timer);
  }, [posterLoaded]);

  // Scroll-driven camera: DOM-based segment progress + optional damping
  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const DAMP_ALPHA = reduceMotion ? 1 : 0.16;

    const refreshSectionTops = () => {
      sectionTopsRef.current = sections.map((s) => document.getElementById(s.id)?.offsetTop ?? 0);
    };

    const computeTargetCamera = (scrollY: number): CameraPosition => {
      const tops = sectionTopsRef.current;
      const n = sections.length;
      if (tops.length !== n || n === 0) {
        return sections[0].camera;
      }

      if (scrollY <= tops[0]) {
        return sections[0].camera;
      }

      for (let i = 0; i < n - 1; i++) {
        const start = tops[i];
        const end = tops[i + 1];
        if (scrollY < end) {
          const span = end - start;
          const t = span > 0 ? (scrollY - start) / span : 0;
          return lerpCamera(sections[i].camera, sections[i + 1].camera, t);
        }
      }

      return sections[n - 1].camera;
    };

    const applyCamera = (cam: CameraPosition) => {
      const r = cam.rotate ?? 0;
      const transform = `translate(${cam.x}%, ${cam.y}%) scale(${cam.scale}) rotate(${r}deg)`;

      if (videoRef.current) videoRef.current.style.transform = transform;
      if (posterRef.current) posterRef.current.style.transform = transform;

      if (vignetteRef.current) {
        const vx = 50 - cam.x * 0.3;
        const vy = 65 - cam.y * 0.3;
        vignetteRef.current.style.background = `radial-gradient(ellipse at ${vx}% ${vy}%, transparent 10%, rgba(0,0,0,0.3) 35%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,0.95) 100%)`;
      }
    };

    const tick = () => {
      const scrollY = window.scrollY;
      const target = computeTargetCamera(scrollY);

      if (reduceMotion) {
        applyCamera(target);
        smoothedRef.current = { ...target };
        rafRef.current = 0;
        return;
      }

      if (!smoothedRef.current) {
        smoothedRef.current = { ...target };
        applyCamera(target);
      }

      const next = dampCamera(smoothedRef.current, target, DAMP_ALPHA);
      smoothedRef.current = next;
      applyCamera(next);

      if (cameraDistance(next, target) > 0.015) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        smoothedRef.current = { ...target };
        applyCamera(target);
        rafRef.current = 0;
      }
    };

    const scheduleTick = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(tick);
    };

    const handleScroll = () => {
      scheduleTick();
    };

    const handleResize = () => {
      refreshSectionTops();
      scheduleTick();
    };

    refreshSectionTops();
    smoothedRef.current = null;
    scheduleTick();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      smoothedRef.current = null;
    };
  }, []);

  // Determine which poster image to show: LQIP → real poster (WebP + JPEG via image-set)
  const posterBg =
    posterLoaded && (posterSrc || posterWebpSrc)
      ? posterWebpSrc && posterSrc
        ? `image-set(url("${posterWebpSrc}") type("image/webp"), url("${posterSrc}") type("image/jpeg"))`
        : `url(${posterWebpSrc || posterSrc})`
      : LQIP;

  return (
    <div className="video-bg">
      {/* Tier 1+2: LQIP (instant) → poster WebP + JPEG (preloaded) */}
      <div
        ref={posterRef}
        className={`video-bg__poster ${isReady ? 'video-bg__poster--hidden' : ''}`}
        style={{ backgroundImage: `url(${posterBg})` }}
      />

      {/* Tier 3: Video — WebM preferred (2.5MB), MP4 fallback (17MB) */}
      {!skipVideo.current && (
        <video
          ref={videoRef}
          className={`video-bg__video ${isReady ? 'video-bg__video--visible' : ''}`}
          muted
          loop
          playsInline
          preload="none"
          onCanPlay={handleCanPlay}
        >
          {webmSrc && <source src={webmSrc} type="video/webm" />}
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}

      {/* Dynamic vignette — shifts with camera for depth */}
      <div ref={vignetteRef} className="video-bg__vignette" />

      {/* Teal / blue / violet washes — extra depth vs flat black */}
      <div className="video-bg__atmosphere" aria-hidden />

      {/* Static gradient overlay for text readability */}
      <div className="video-bg__overlay" />

      {/* Procedural film grain (SVG turbulence) — no image asset */}
      <div className="video-bg__grain" aria-hidden />
    </div>
  );
}
